import type { DependencyList, RefCallback } from 'react';
import type { HotkeyCallback, Keys, Options } from 'react-hotkeys-hook';
// eslint-disable-next-line no-restricted-imports -- sole sanctioned import; all app code uses useClarionHotkeys
import { useHotkeys } from 'react-hotkeys-hook';
import { usePref } from './prefs';

/** @returns whether the user has keyboard shortcuts enabled */
export function useKeyboardShortcutsEnabled(): boolean {
  const [enabled] = usePref('keyboard.shortcutsEnabled', true);
  return enabled;
}

/**
 * Wraps `useHotkeys` from `react-hotkeys-hook`, but respects the `keyboard.shortcutsEnabled` preference.
 */
export function useClarionHotkeys<T extends HTMLElement = HTMLElement>(
  keys: Keys,
  callback: HotkeyCallback,
  options: Options & { description: string }, // Make description required for hotkeys dialog
  deps?: DependencyList,
): RefCallback<T | null> {
  const shortcutsEnabled = useKeyboardShortcutsEnabled();
  const mergedOptions = withEnabled(shortcutsEnabled, options);
  return useHotkeys(keys, callback, mergedOptions, deps ?? []);
}

function mergeEnabled(shortcutsEnabled: boolean, consumerEnabled: Options['enabled']) {
  if (!shortcutsEnabled) return false;
  return consumerEnabled ?? true;
}

function withEnabled(shortcutsEnabled: boolean, options: Options): Options {
  return { ...options, enabled: mergeEnabled(shortcutsEnabled, options.enabled) };
}
